import { Link } from "react-router-dom";
import { costTrajectoryDatasetSchema } from "@/lib/schema";
import HeroImg from "@/components/HeroImg";

const slug = "landscaping-cost-south-carolina-2026";
const yoyPoints = [{"year":2022,"value":7800},{"year":2023,"value":8900},{"year":2024,"value":9600},{"year":2025,"value":9800},{"year":2026,"value":10000}];

const guide = {
  slug,
  title: "Landscaping Cost in South Carolina 2026",
  description: "What landscaping actually costs in South Carolina in 2026 — typical ranges for front-yard refreshes, full-yard designs, and irrigation by scope, the three biggest local cost drivers, full breakdown, and five ways to save.",
  category: "Cost Guide",
  date: "2026-05-09",
  dateModified: "2026-06-01",
  readingMinutes: 7,
  heroImage: "/landscape-heroes/south-carolina.png",
  related: ["landscape"],
  relatedStates: ["SC"],
  // Time-series JSON-LD so AI crawlers (Google AI Overviews, Perplexity,
  // ChatGPT search) can extract the multi-year cost trajectory as structured
  // data — not just the visible HTML table.
  extraSchema: [
    costTrajectoryDatasetSchema({
      title: "South Carolina landscaping cost — 2022-2026 mid-range trajectory",
      description: "Annual typical mid-range landscaping project cost in South Carolina, 2022-2026. Sourced from BLS regional labor data, 2026 Remodeling Magazine Cost vs Value Report, and aggregated contractor pricing, scaled by South Carolina's 0.95× cost index.",
      path: `/guides/${slug}`,
      points: yoyPoints,
    }),
  ],
  Body: () => (
    <>
      <p className="text-xs font-medium uppercase tracking-wider text-slate-500" data-testid="guide-last-updated">
        Last updated · May 9, 2026 · South Carolina cost-index 0.95×
      </p>
      <p>
        South Carolina runs ~5% below national — Charleston coastal premium offsets cheaper inland markets. A typical full-yard mid-grade landscape design with planting + sod that nationally averages $6,000-$16,000 lands at $5,700–$17,500 for most South Carolina homeowners in 2026. Below: the real numbers, the
        three biggest local cost drivers, and the moves that actually reduce your final bill.
      </p>

      <h2>The headline numbers for 2026</h2>
      <p>
        Based on contractor pricing data, BLS regional labor rates, and project-specific market
        benchmarks, here's what a landscaping costs across South Carolina:
      </p>
      <ul>
        <li><strong>Front-yard refresh (planting beds + mulch):</strong> $2,000–$6,800</li>
        <li><strong>Full-yard design + sod + planting:</strong> $5,700–$17,500</li>
        <li><strong>Full-yard + irrigation + landscape lighting:</strong> $10,000–$31,900</li>
      </ul>
      <p>
        These reflect South Carolina's state-level cost factor of 0.95× the national
        baseline, mid-range quality, with a standard 10% contingency. Budget-grade runs 20–30%
        lower; high-end scope and premium materials push 60–90% higher. Run our{" "}
        <Link to="/landscaping-cost-south-carolina">South Carolina landscaping cost calculator</Link>{" "}
        for a state-adjusted estimate.
      </p>
      <p className="text-sm text-slate-500">
        <em>
          Cost ranges sourced from contractor pricing data, Bureau of Labor Statistics regional labor rates, and 2026
          industry cost-vs-value benchmarks for landscaping.
        </em>
      </p>

      <h2>Why South Carolina landscaping pricing looks the way it does</h2>
      <p>Three state-level factors drive the spread:</p>
      <ol>
        <li>
          <strong>Charleston metro labor.</strong> Charleston-metro trade rates run $52–$74/hr. Columbia, Greenville, and inland SC trend $8–$14/hr below Charleston.
        </li>
        <li>
          <strong>Coastal storm code.</strong> Charleston and coastal counties require hurricane-rated fastening and elevated electrical for flood-zone areas. Adds 5–10% on relevant trades.
        </li>
        <li>
          <strong>Strong in-migration since 2020.</strong> Hilton Head, Myrtle Beach, and Charleston suburbs have seen meaningful trade-rate climbs from in-migration — typical 10–20% increase since 2020.
        </li>
      </ol>

      <div className="my-10 overflow-hidden rounded-2xl border border-slate-200">
        <HeroImg
          src="/landscape-heroes/south-carolina.png"
          alt="South Carolina landscaping reference photo"
          loading="lazy"
          className="h-full w-full object-cover"
        />
        <p className="bg-slate-50 px-4 py-2 text-xs text-slate-500">
          Representative landscaping in South Carolina. Realistic 2026 budget for the typical
          scope shown: $5,700–$17,500.
        </p>
      </div>

      <h2>Full cost breakdown: full-yard design + sod + planting, South Carolina</h2>
      <p>Here's what the $5,700–$17,500 range looks like split into actual line items:</p>
      <div className="my-6 overflow-hidden rounded-2xl border border-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-5 py-3">Category</th>
              <th className="px-5 py-3 text-right">Low</th>
              <th className="px-5 py-3 text-right">High</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 text-slate-700">
            <tr>
              <td className="px-5 py-3 font-medium">Labor (50%)</td>
              <td className="px-5 py-3 text-right tabular-nums">$2,850</td>
              <td className="px-5 py-3 text-right tabular-nums">$8,750</td>
            </tr>
            <tr>
              <td className="px-5 py-3 font-medium">Plants + sod + mulch + irrigation parts (45%)</td>
              <td className="px-5 py-3 text-right tabular-nums">$1,995</td>
              <td className="px-5 py-3 text-right tabular-nums">$6,125</td>
            </tr>
            <tr>
              <td className="px-5 py-3 font-medium">Permits &amp; fees (5%)</td>
              <td className="px-5 py-3 text-right tabular-nums">$285</td>
              <td className="px-5 py-3 text-right tabular-nums">$875</td>
            </tr>
            <tr>
              <td className="px-5 py-3 font-medium">Contingency (10%)</td>
              <td className="px-5 py-3 text-right tabular-nums">$570</td>
              <td className="px-5 py-3 text-right tabular-nums">$1,750</td>
            </tr>
            <tr className="bg-slate-900 text-white">
              <td className="px-5 py-3 font-display text-base font-semibold">Total estimated range</td>
              <td className="px-5 py-3 text-right font-display text-base font-semibold tabular-nums">$5,700</td>
              <td className="px-5 py-3 text-right font-display text-base font-semibold tabular-nums">$17,500</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h2>Five ways to actually save money on a South Carolina landscaping</h2>
      <ol>
        <li>
          <strong>Plan around South Carolina's biggest cost driver.</strong> Charleston-metro trade rates run $52–$74/hr. Columbia, Greenville, and inland SC trend $8–$14/hr below Charleston.
        </li>
        <li>
          <strong>Account for the second-largest driver.</strong>{" "}
          Charleston and coastal counties require hurricane-rated fastening and elevated electrical for flood-zone areas. Adds 5–10% on relevant trades.
        </li>
        <li>
          <strong>DIY mulch + irrigation tie-in.</strong> Mulch placement is unskilled work that crews charge $40-$60 per cubic yard installed. Buying bulk mulch (~$25 per cubic yard delivered) and spreading it yourself saves $400-$800. Drip-irrigation tie-in from an existing valve is a half-day weekend job that crews charge $1,200-$2,200 for.
        </li>
        <li>
          <strong>Plant in fall, not spring.</strong> Most nurseries discount end-of-season plant material 30-50% in October and November. The plants establish through winter dormancy and explode in spring just like a March planting — at half the cost.
        </li>
        <li>
          <strong>Plan for low-maintenance native plants.</strong> Native species use 30-60% less water and require 50-70% less ongoing maintenance than ornamental imports. The upfront cost is similar; the 10-year total cost of ownership is dramatically lower (and resale appraisers in drought-prone states now explicitly value xeriscape-ready yards).
        </li>
      </ol>

      <h2>Timeline expectations</h2>
      <p>Most South Carolina landscape jobs take 4-10 working days. A planting-bed refresh runs 1-2 days. A full-yard design + planting + sod runs 5-7 days. Adding irrigation adds 2-4 days. Lighting + smart-controller add 1-2 days.</p>

      <h2>South Carolina landscaping cost — 4-year trajectory</h2>
      <p>
        South Carolina landscaping pricing rose +28.2% from 2022
        to 2026, from $7,800 to $10,000 on a typical mid-range project.
        Year-over-year detail:
      </p>
      <div className="my-4 overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-slate-300 bg-slate-50 text-left">
              <th className="px-3 py-2 font-semibold">Year</th>
              <th className="px-3 py-2 font-semibold">Typical mid-range total</th>
              <th className="px-3 py-2 font-semibold">YoY change</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            <tr>
              <td className="px-3 py-2">2022</td>
              <td className="px-3 py-2 tabular-nums">$7,800</td>
              <td className="px-3 py-2 text-slate-500">—</td>
            </tr>
            <tr>
              <td className="px-3 py-2">2023</td>
              <td className="px-3 py-2 tabular-nums">$8,900</td>
              <td className="px-3 py-2 text-rose-700 tabular-nums">+14.1%</td>
            </tr>
            <tr>
              <td className="px-3 py-2">2024</td>
              <td className="px-3 py-2 tabular-nums">$9,600</td>
              <td className="px-3 py-2 text-rose-700 tabular-nums">+7.9%</td>
            </tr>
            <tr>
              <td className="px-3 py-2">2025</td>
              <td className="px-3 py-2 tabular-nums">$9,800</td>
              <td className="px-3 py-2 text-rose-700 tabular-nums">+2.1%</td>
            </tr>
            <tr>
              <td className="px-3 py-2 font-medium">2026 <span className="text-xs text-slate-400">(projected)</span></td>
              <td className="px-3 py-2 tabular-nums">$10,000</td>
              <td className="px-3 py-2 text-rose-700 tabular-nums">+2%</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="my-5 rounded-2xl border-l-4 border-amber-500 bg-amber-50/60 p-5">
        <p className="text-xs font-semibold uppercase tracking-wider text-amber-700">
          Why landscaping pricing rose, then stabilized
        </p>
        <p className="mt-2 text-sm leading-relaxed text-slate-700">
          Nursery and plant-material pricing spiked 18-22% across 2022-2023 as peat-moss, potting-mix, and freight costs all rose simultaneously. Irrigation-tubing and copper backflow assemblies tracked metals pricing. Sod has been the most stable input, but installer labor (the dominant share of any landscape budget) has compounded 6-8%/yr across the period. By 2025 materials had stabilized; labor continues to drift, and irrigation crews remain booked 8-12 weeks out in most metros.
        </p>
      </div>

      <h2>South Carolina vs. neighboring states</h2>
      <p>
        How does South Carolina compare to its direct neighbors? The numbers below reflect overall renovation cost
        differences — useful context if your project lives near a state line.
      </p>
      <ul className="my-5 space-y-2 list-none p-0">
          <li className="flex items-center justify-between gap-3 rounded-lg bg-slate-50 px-3 py-2 text-sm">
            <span className="text-slate-700">vs. <strong className="text-slate-900">North Carolina</strong> (0.98×)</span>
            <span className="tabular-nums"><strong className="text-emerald-700">3% cheaper</strong> in North Carolina</span>
          </li>
          <li className="flex items-center justify-between gap-3 rounded-lg bg-slate-50 px-3 py-2 text-sm">
            <span className="text-slate-700">vs. <strong className="text-slate-900">Georgia</strong> (0.96×)</span>
            <span className="tabular-nums"><strong className="text-slate-600">≈ same range</strong></span>
          </li>
      </ul>


      <h2>FAQ — landscaping in South Carolina</h2>
      <div className="my-5 space-y-5 rounded-2xl bg-slate-50 p-6">
        <div>
          <p className="text-sm font-semibold text-slate-900">How much does landscaping cost in South Carolina in 2026?</p>
          <p className="mt-1 text-sm leading-relaxed text-slate-600">Typical landscaping pricing in South Carolina runs $5,700–$17,500 for a full-yard design + sod + planting, mid-range scope. Budget-grade work lands 20–30% lower; high-end scope and premium materials push 60–90% higher.</p>
        </div>
        <div>
          <p className="text-sm font-semibold text-slate-900">Do I need a permit for landscaping in South Carolina?</p>
          <p className="mt-1 text-sm leading-relaxed text-slate-600">Most South Carolina municipalities require a permit for any work involving plumbing, electrical, structural change, or roof tear-off. Cosmetic-only updates typically don't. Permit fees commonly run $150–$600 in South Carolina depending on jurisdiction.</p>
        </div>
        <div>
          <p className="text-sm font-semibold text-slate-900">When is the cheapest time to schedule landscaping in South Carolina?</p>
          <p className="mt-1 text-sm leading-relaxed text-slate-600">Late fall and winter are typically the quietest scheduling windows in South Carolina — contractor bids run 5–15% softer than in spring/summer peak season. Booking 6–10 weeks ahead of your target start date usually unlocks the best pricing.</p>
        </div>
        <div>
          <p className="text-sm font-semibold text-slate-900">Is South Carolina an expensive state for this project?</p>
          <p className="mt-1 text-sm leading-relaxed text-slate-600">South Carolina sits within a few percent of the U.S. national average. The state's overall cost-index factor of 0.95× the national baseline drives the spread.</p>
        </div>
      </div>

      <h2>The bottom line for South Carolina homeowners</h2>
      <p>
        South Carolina sits within a few percent of the U.S. national average — your zip code, contractor pool, and permit
        jurisdiction matter as much as the state average. Knowing the realistic
        state-specific number lets you tell a fair quote from an inflated one. Get a
        state-adjusted breakdown in 60 seconds with our{" "}
        <Link to="/outdoor-renovation-estimator">free landscaping cost calculator</Link>, then collect
        three written bids from licensed local contractors before signing anything.
      </p>
    </>
  ),
};

export default guide;
